import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { AppClickToCallPhone as PhoneIconLib } from '@elfsight/icons';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import { changeColorAlpha, Show } from '@elfsight/lib-utils';
import { FreeLink, pickFreeLinkSettings } from '@elfsight/widget-free-link';
import { getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import classNames from 'classnames';
import { formatNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { positions } from '../../constants';
import { meta } from '../../meta';
import { Header } from './Header';

export type WindowProps = {
  show: boolean;
};

export function Window({ show }: WindowProps) {
  const {
    phone,
    windowText,
    windowFormatPhone,
    position: positionSetting,
    windowBackgroundColor,
    windowTextColor,
    windowPhoneColor,
    windowTextFontSize,
    windowPhoneFontSize,
    googleAnalytics,
    widgetId,
    ...settings
  } = useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const lastShowRef = useRef(false);
  const { position, align } = positions[positionSetting];

  const phoneURL = parsePhoneNumberFromString(phone)
    ? parsePhoneNumberFromString(phone).getURI()
    : `tel:${phone}`;

  const phoneText =
    parsePhoneNumberFromString(phone) && windowFormatPhone
      ? formatNumber(phone, 'NATIONAL')
      : phone;

  useEffect(() => {
    if (!lastShowRef.current && show) {
      sendGoogleAnalyticsEvents({
        googleAnalyticsEvents: googleAnalytics,
        eventType: 'widgetOpened',
        valueMapping: { widgetId }
      });
    }
    lastShowRef.current = show;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Component
      _show={show}
      _displayFreeLink={freeLinkSettings.displayFreeLink}
      _position={position}
      _align={align}
      _backgroundColor={windowBackgroundColor}
    >
      <Header />

      <Content
        _fontSize={windowTextFontSize}
        _color={changeColorAlpha(windowTextColor, -0.1)}
      >
        <Show when={!!windowText}>
          <Text
            dangerouslySetInnerHTML={{
              __html: windowText
            }}
          />
        </Show>

        <Show when={!!phone}>
          <Phone
            href={phoneURL}
            onClick={() =>
              sendGoogleAnalyticsEvents({
                googleAnalyticsEvents: googleAnalytics,
                eventType: 'callClicked',
                valueMapping: { widgetId }
              })
            }
            _fontSize={windowPhoneFontSize}
            _phoneColor={windowPhoneColor}
          >
            <PhoneIcon _fontSize={windowPhoneFontSize} />
            <PhoneText _fontSize={windowPhoneFontSize}>{phoneText}</PhoneText>
          </Phone>
        </Show>
      </Content>

      <FreeLink
        {...freeLinkSettings}
        appAlias={meta.alias}
        appName={meta.name}
        mode="text"
        textModeProps={{
          color: getInvertedColor(windowBackgroundColor),
          fontSize: 9,
          forceTwoLines: true
        }}
        iconHidden
        style={{
          link: {
            marginBlock: '20px 0'
          }
        }}
      />
    </Component>
  );
}

const Component = styled.div.attrs(({ _show }) => ({
  className: classNames(`${meta.prefix}-window-component`, {
    [`${meta.prefix}-window-show`]: _show
  })
}))`
  opacity: 0;
  top: 0;
  transition: 0.3s ease all;
  pointer-events: none;
  position: absolute;
  transform: translateY(-100%);
  min-width: 260px;
  background-color: ${({ _backgroundColor }) => _backgroundColor || '#fff'};
  box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border-radius: 8px;
  padding: 20px 24px 32px;
  box-sizing: border-box;
  ${({ _position, _align }) =>
    _position === 'floating' ? `${_align}: 0;` : 'left: 0;'}

  ${({ _displayFreeLink }) => _displayFreeLink && 'padding-bottom: 20px;'}

  ${({ _show }) =>
    _show &&
    css`
      opacity: 1;
      top: -12px;
      pointer-events: all;
      transform: translateY(calc(-100% - 4px));
    `}
`;

const Content = styled.div.attrs({
  className: `${meta.prefix}-window-content`
})`
  font-size: ${({ _fontSize }) => _fontSize}px;
  line-height: 1.357142;
  text-align: center;
  margin-top: 14px;
  color: ${({ _color }) => _color};
`;

const Text = styled.div.attrs({
  className: `${meta.prefix}-window-text`
})`
  display: block;
  margin-bottom: 18px;
`;

const Phone = styled.a.attrs({
  className: `${meta.prefix}-window-phone`
})`
  display: inline-flex;
  text-decoration: none;
  padding: ${({ _fontSize }) => `${_fontSize * 0.1}px ${_fontSize}px`};
  margin-top: 0;
  border-radius: 200px;
  align-items: center;
  transition: 0.3s ease all;
  background-color: ${({ _phoneColor }) =>
    changeColorAlpha(_phoneColor, -0.94)};
  color: ${({ _phoneColor }) => _phoneColor};

  &:hover {
    text-decoration: none !important;
    background-color: ${({ _phoneColor }) =>
      changeColorAlpha(_phoneColor, -0.9)};
  }
`;

const PhoneIcon = styled(PhoneIconLib).attrs({
  className: `${meta.prefix}-window-phoneIcon`
})`
  width: ${({ _fontSize }) => _fontSize * 0.7}px;
  height: ${({ _fontSize }) => _fontSize * 0.7}px;
  display: flex;
  justify-content: center;
  align-items: center;
  fill: currentColor;
`;

const PhoneText = styled.div.attrs({
  className: `${meta.prefix}-window-phoneText`
})`
  font-size: ${({ _fontSize }) => _fontSize}px;
  line-height: 1.4;
  font-weight: 700;
  text-decoration: none;
  margin-left: ${({ _fontSize }) => _fontSize * 0.4}px;
  white-space: nowrap;

  ${Phone}:hover & {
    text-decoration: none !important;
  }
`;
